import {
  DomainConfigSchema,
  DomainPathRuleSchema,
  DomainSourceSchema,
} from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';
import {
  domainScopeConflictsWithPathRules,
  normalizeDomainConfigInput,
} from './domain-config-sync.util';
import { isValidDomain, isValidScopePath } from './domain-validation';

const pathRuleThirdPartyFlag = z
  .boolean()
  .nullable()
  .optional()
  .transform((value) => value ?? false);

export const DomainPathRuleInputSchema = DomainPathRuleSchema.extend({
  isInFunctionalityScope: z.boolean().optional(),
  isInTestingScope: z.boolean().optional(),
  isThirdParty: pathRuleThirdPartyFlag,
});

export const DomainConfigReturnSchema = DomainConfigSchema.extend({
  pathRules: DomainPathRuleSchema.extend({
    isThirdParty: pathRuleThirdPartyFlag,
  })
    .array()
    .default([]),
  source: DomainSourceSchema.nullable().transform(
    (source) => source ?? DomainSourceSchema.enum.USER_PROVIDED,
  ),
  isThirdParty: z.boolean().nullable().optional().default(null),
});

export const DomainConfigInputSchema = DomainConfigSchema.extend({
  source: DomainSourceSchema,
  isInFunctionalityScope: z.boolean().nullable().optional(),
  isInTestingScope: z.boolean().nullable().optional(),
  isThirdParty: z.boolean().nullable().optional().default(null),
  pathRules: DomainPathRuleInputSchema.array().optional(),
  hasWaf: z.boolean().nullable().optional(),
  createdAt: z.coerce.date().optional(),
  enabledAt: z.coerce.date().nullable().optional(),
})
  .superRefine((entry, ctx) => {
    if (!isValidDomain(entry.domain)) {
      ctx.addIssue({
        code: 'custom',
        message: `Invalid domain: ${entry.domain}`,
        path: ['domain'],
      });
    }

    if (domainScopeConflictsWithPathRules(entry)) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Domain-level testing scope cannot be set when allowlist path rules are configured',
        path: ['pathRules'],
      });
    }

    const paths = (entry.pathRules ?? []).map((rule) => rule.path);
    const uniquePaths = new Set(paths);

    if (uniquePaths.size !== paths.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'Duplicate scope paths are not allowed',
        path: ['pathRules'],
      });
    }

    for (const [index, path] of paths.entries()) {
      if (!isValidScopePath(path)) {
        ctx.addIssue({
          code: 'custom',
          message: `Invalid scope path: ${path}`,
          path: ['pathRules', index, 'path'],
        });
      }
    }
  })
  .transform((entry) => normalizeDomainConfigInput(entry));

export const DomainConfigListSchema = DomainConfigInputSchema.array();
